import React from "react";
import styled from "styled-components";
import {
  RDVButton,
  Inner,
  SubtitleSection,
} from "../../components/commonWidgets";
import HighlightTitle from "../../components/HighlightTitle";
import { Modal, Box, TextField, Button } from "@mui/material";
import ReCAPTCHA from "react-google-recaptcha";
import theme from "../../theme";
import AnimateOnScroll from "../../components/AnimateOnScroll";
import GuideLeadForm from "../../components/GuideLeadForm";

const Section = styled.section`
  /*background: var(--my-color3);*/
  width: 100%;
  padding: 3rem 0;
  color: white;
  position: relative;
  overflow: hidden;

  /* texture overlay */
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image: radial-gradient(
      rgba(255, 255, 255, 0.12) 1px,
      transparent 1px
    );
    background-size: 4px 4px;
    opacity: 0.3;
    pointer-events: none;
  }
`;

const Container = styled(Inner)`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: auto;
  max-width: 800px;
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  margin-top: 1rem;
  color: var(--my-color5);
  font-weight: bold;
`;

const BenefitsContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 2rem;
  margin: 2rem 0;

  ${theme.breakpoints.down("sm")} {
    flex-direction: column;
    gap: 1rem;
  }
`;

const Benefit = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  color: white;

  &::before {
    content: "✓";
    color: var(--my-color5);
    font-weight: bold;
    font-size: 1.2rem;
  }
`;

const CTAContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  flex-wrap: wrap;
  justify-content: center;

  ${theme.breakpoints.down("sm")} {
    flex-direction: column;
    align-items: center;
  }
`;

const SecondaryButton = styled.button`
  padding: 0.6rem 1.1rem;
  background-color: transparent;
  color: white;
  text-decoration: none;
  border: 2px solid white;
  border-radius: 8px;
  transition: all 0.3s ease;
  font-weight: 400;
  cursor: pointer;
  font-size: 1.1rem;
  white-space: nowrap;
  height: fit-content;
  width: fit-content;
  text-transform: uppercase;

  &:hover {
    background-color: white;
    color: var(--my-color3);
  }
`;

const FinalCTASection = () => {
  const benefits = [
    "Audit gratuit de votre présence en ligne",
    "Stratégie personnalisée en 48h",
    "Garantie satisfaction ou remboursé",
  ];

  const [open, setOpen] = React.useState(false);
  const [email, setEmail] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [error, setError] = React.useState("");
  const [recaptchaToken, setRecaptchaToken] = React.useState(null);

  const handleDownloadClick = () => setOpen(true);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      setError("Merci de saisir votre email.");
      return;
    }
    if (!recaptchaToken) {
      setError("Veuillez valider le reCAPTCHA.");
      return;
    }
    setSending(true);

    const data = new FormData();
    data.append("offer", "Guide PDF");
    data.append("name", "Visiteur du site");
    data.append("email", email);
    data.append("message", "Téléchargement du guide");
    data.append("g-recaptcha-response", recaptchaToken);

    try {
      const response = await fetch(
        "https://www.digibuddy.fr/scripts/sendContact.php",
        {
          method: "POST",
          body: data,
        }
      );
      if (!response.ok) throw new Error(response.status);
      // we don't need to parse result; assume ok
      window.open("/guide.pdf", "_blank");
      setOpen(false);
      setEmail("");
      setRecaptchaToken(null);
    } catch (e) {
      console.error(e);
      setError("Erreur d'envoi. Merci de réessayer.");
    }
    setSending(false);
  };

  return (
    <Section>
      <Container>
        <AnimateOnScroll type="fade">
          <HighlightTitle
            text="Prêt à transformer ?"
            highlightWord="transformer"
            bg="white"
            rotateText={false}
            mainTextColor="white"
            textColor="var(--my-color3)"
          />
        </AnimateOnScroll>
        <AnimateOnScroll type="fade">
          <SubtitleSection
            style={{
              color: "var(--my-color5)",
              fontStyle: "italic",
              marginTop: "0.6rem",
            }}
          >
            Votre présence en ligne peut devenir une machine à convertir.
          </SubtitleSection>
        </AnimateOnScroll>
        <AnimateOnScroll type="fade">
          <BenefitsContainer>
            {benefits.map((benefit, index) => (
              <Benefit key={index}>{benefit}</Benefit>
            ))}
          </BenefitsContainer>
        </AnimateOnScroll>

        <AnimateOnScroll type="fade">
          <CTAContainer>
            <RDVButton label="Planifier un appel" />
            {false && (
              <SecondaryButton onClick={handleDownloadClick}>
                Télécharger le guide PDF
              </SecondaryButton>
            )}
          </CTAContainer>
        </AnimateOnScroll>
        <AnimateOnScroll type="fade">
          <GuideLeadForm source="home-cta" />
        </AnimateOnScroll>

        {/* Modal */}
        <Modal open={open} onClose={() => setOpen(false)}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "background.paper",
              p: 4,
              borderRadius: 2,
              boxShadow: 24,
              width: 300,
            }}
          >
            <form
              onSubmit={handleSubmit}
              style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
            >
              <h3
                style={{
                  margin: 0,
                  textAlign: "center",
                  color: "var(--my-color3)",
                }}
              >
                Recevoir le guide
              </h3>
              <TextField
                label="Email"
                type="email"
                size="small"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {error && (
                <span style={{ color: "red", fontSize: "0.85rem" }}>
                  {error}
                </span>
              )}
              <ReCAPTCHA
                sitekey="6LdbgDMrAAAAAMFZgiPxEzSevx5W2suOmIFgxPqF"
                onChange={(token) => setRecaptchaToken(token)}
              />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={sending}
              >
                {sending ? "Envoi…" : "Télécharger"}
              </Button>
            </form>
          </Box>
        </Modal>
      </Container>
    </Section>
  );
};

export default FinalCTASection;
